/**
 * App Check recovery utilities (admin portal).
 *
 * Mirrors apps/client/src/lib/appCheckRecovery.ts. Per CLAUDE.md anti-pattern
 * guard ("three similar lines > premature abstraction"), duplicating into the
 * admin bundle is correct — two consumers do not justify a shared package.
 *
 * Background:
 *   When App Check rejects a token (HTTP 403), the Firebase JS SDK throttles
 *   token-exchange attempts client-side for up to 24 hours and surfaces opaque
 *   `auth/firebase-app-check-token-is-invalid` errors with no actionable UI.
 *
 * This module:
 *   1. Classifies auth errors so the LoginPage can route to a recovery banner.
 *   2. Wipes the IndexedDB throttle state so the user can retry immediately
 *      after fixing the underlying cause (extension, multi-account, DevTools).
 *   3. Provides a SHA-256 short-hash for telemetry without leaking PII.
 */

export interface AppCheckErrorContext {
  isAppCheckFailure: boolean;
  throttleRemainingMs: number | null;
  isInitialThrottle: boolean;
  raw: unknown;
}

interface ErrorLike {
  code?: string;
  message?: string;
}

const isErrorLike = (value: unknown): value is ErrorLike => {
  return typeof value === 'object' && value !== null;
};

const readString = (obj: ErrorLike, key: keyof ErrorLike): string => {
  const raw = obj[key];
  return typeof raw === 'string' ? raw : '';
};

/**
 * Returns the duration in milliseconds parsed from a throttle hint embedded in
 * Firebase App Check error messages. Recognises both day-prefixed and
 * hour-prefixed forms emitted by the SDK.
 *
 *   "Attempts allowed again after 0d:23h:59m:42s"
 *   "Attempts allowed again after 23h:59m:42s"
 *
 * Returns `null` when no hint is present.
 */
const parseThrottleRemainingMs = (message: string): number | null => {
  const dayMatch = message.match(/Attempts allowed again after (\d+)d:(\d+)h:(\d+)m:(\d+)s/);
  if (dayMatch) {
    const days = Number(dayMatch[1]);
    const hours = Number(dayMatch[2]);
    const minutes = Number(dayMatch[3]);
    const seconds = Number(dayMatch[4]);
    return ((days * 24 + hours) * 3600 + minutes * 60 + seconds) * 1000;
  }
  const hourMatch = message.match(/Attempts allowed again after (\d+)h:(\d+)m:(\d+)s/);
  if (hourMatch) {
    const hours = Number(hourMatch[1]);
    const minutes = Number(hourMatch[2]);
    const seconds = Number(hourMatch[3]);
    return (hours * 3600 + minutes * 60 + seconds) * 1000;
  }
  return null;
};

const isAppCheckError = (code: string, message: string): boolean => {
  if (code === 'auth/firebase-app-check-token-is-invalid') return true;
  if (code === 'auth/internal-error' && /app[- ]check/i.test(message)) return true;
  if (/AppCheck: (Requests throttled|403 error)/.test(message)) return true;
  return false;
};

/**
 * Classifies an unknown error thrown by Firebase Auth as either an App Check
 * rejection (with recoverable context) or anything else.
 */
export const classifyAuthError = (err: unknown): AppCheckErrorContext => {
  if (!isErrorLike(err)) {
    return {
      isAppCheckFailure: false,
      throttleRemainingMs: null,
      isInitialThrottle: false,
      raw: err,
    };
  }
  const code = readString(err, 'code');
  const message = readString(err, 'message');
  const isAppCheckFailure = isAppCheckError(code, message);
  if (!isAppCheckFailure) {
    return {
      isAppCheckFailure: false,
      throttleRemainingMs: null,
      isInitialThrottle: false,
      raw: err,
    };
  }
  return {
    isAppCheckFailure: true,
    throttleRemainingMs: parseThrottleRemainingMs(message),
    isInitialThrottle: message.includes('appCheck/initial-throttle'),
    raw: err,
  };
};

const deleteDatabaseSafe = (factory: IDBFactory, name: string): Promise<void> => {
  return new Promise<void>((resolve) => {
    let settled = false;
    const settle = (): void => {
      if (settled) return;
      settled = true;
      resolve();
    };
    try {
      const request = factory.deleteDatabase(name);
      request.onsuccess = settle;
      request.onerror = settle;
      request.onblocked = settle;
    } catch {
      settle();
    }
  });
};

interface DatabaseInfoLike {
  name?: string;
  version?: number;
}

/**
 * Wipes the Firebase App Check IndexedDB throttle state. The SDK persists
 * "next allowed attempt" timestamps in `firebase-app-check-database` (and
 * adjacent stores); deleting them lets the next token-exchange call run
 * immediately. Safe no-op on browsers without `indexedDB.databases()`.
 *
 * `IDBFactory.databases` is declared non-optional in lib.dom but is in fact
 * a Chromium-era addition not implemented in older Safari/Firefox builds, so
 * we feature-detect at runtime via a structural cast.
 */
export const clearAppCheckThrottleState = async (): Promise<void> => {
  if (typeof indexedDB === 'undefined') return;
  const enumerator = (indexedDB as { databases?: () => Promise<DatabaseInfoLike[]> }).databases;
  if (typeof enumerator !== 'function') return;
  let databases: DatabaseInfoLike[] = [];
  try {
    databases = await enumerator.call(indexedDB);
  } catch {
    return;
  }
  const targets = databases
    .map((entry) => entry.name)
    .filter((name): name is string => typeof name === 'string' && name.startsWith('firebase-app-check'));
  await Promise.all(targets.map((name) => deleteDatabaseSafe(indexedDB, name)));
};

/**
 * SHA-256 hashes the given string, returning the first 16 hex chars. Used as
 * a stable, non-reversible user identifier for telemetry. Returns an empty
 * string on browsers without SubtleCrypto (e.g. insecure context).
 */
export const hashEmail = async (email: string): Promise<string> => {
  if (typeof crypto === 'undefined' || typeof crypto.subtle === 'undefined') return '';
  try {
    const encoder = new TextEncoder();
    const data = encoder.encode(email.trim().toLowerCase());
    const digest = await crypto.subtle.digest('SHA-256', data);
    const bytes = new Uint8Array(digest);
    let hex = '';
    for (let i = 0; i < bytes.length; i += 1) {
      const byte = bytes[i].toString(16).padStart(2, '0');
      hex += byte;
    }
    return hex.slice(0, 16);
  } catch {
    return '';
  }
};
